import { Field, type FormikValues, useFormikContext } from "formik";
import get from "lodash/get";
import { type ChangeEvent, useEffect, useState } from "react";
import PillarFormInputWrapper, {
  type PillarFormInputWrapperProps,
} from "shared/components/pillar-form/components/PillarFormInputWrapper";

export type PillarFormCheckboxValue = boolean | undefined;

export type PillarFormCheckboxProps = PillarFormInputWrapperProps & {
  name: string;
  /**
   * Under `triState` the event is the browser's own toggle, so
   * `event.target.checked` does not always agree with the value that gets
   * stored - unchecked -> indeterminate reports `checked: true` while formik is
   * set to `undefined`. `values` is also the snapshot from before the change.
   * Read the field back off formik rather than off either argument.
   */
  onChange?: (e: ChangeEvent<HTMLInputElement>, values: FormikValues) => void;
  disabled?: boolean;
  /**
   * Drives the checkbox from outside formik. Being a plain `boolean` it cannot
   * express indeterminate, so it is not supported alongside `triState` - the
   * value stays pinned to the prop and the cycle can never come to rest.
   */
  isChecked?: boolean;
  readOnly?: boolean;
  /**
   * Adds a third, indeterminate state where `undefined` is meaningful rather
   * than a missing value:
   *
   * - `true` - checked
   * - `undefined` - indeterminate, and the state an unset field starts in
   * - `false` - unchecked
   *
   * Clicking cycles indeterminate -> checked -> unchecked -> indeterminate.
   */
  triState?: boolean;
};

const nextTriStateValue = (
  value: PillarFormCheckboxValue,
): PillarFormCheckboxValue => {
  if (value === undefined) {
    return true;
  }
  return value ? false : undefined;
};

/**
 * A stored `null` - what a JSON column hands back for a flag that was never
 * written - has to read as indeterminate rather than as an explicit unchecked,
 * otherwise loading a saved config turns "not decided" into "off".
 */
const toCheckboxValue = (value: unknown): PillarFormCheckboxValue =>
  value === null || value === undefined ? undefined : Boolean(value);

const PillarFormCheckbox = ({
  name,
  label,
  labelClassName,
  additionalClasses,
  onChange,
  isChecked,
  disabled,
  readOnly,
  triState = false,
  testid,
  ...props
}: PillarFormCheckboxProps) => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  // Held in state rather than a ref so the effect re-runs once the input
  // mounts. `indeterminate` has no React prop, so it is the one piece of state
  // that has to be pushed onto the DOM node; formik stays the source of truth.
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
    null,
  );

  const value = toCheckboxValue(isChecked ?? get(values, name));
  const isIndeterminate = triState && value === undefined;

  useEffect(() => {
    // Only normalized under `triState`, where a stored `null` has to become a
    // dropped key to match the indeterminate box being shown. Two-state fields
    // keep writing back whatever they were given.
    const stored = isChecked ?? get(values, name);
    setFieldValue(name, triState ? toCheckboxValue(stored) : stored);
  }, [isChecked]);

  useEffect(() => {
    if (inputElement) {
      inputElement.indeterminate = isIndeterminate;
    }
  }, [inputElement, isIndeterminate]);

  return (
    <div>
      <PillarFormInputWrapper
        name={name}
        labelClassName={labelClassName}
        additionalClasses={additionalClasses}
        label={label}
        testid={testid}
      >
        <Field
          innerRef={setInputElement}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            // The `disabled` attribute only stops the clicks a user can make -
            // a click dispatched at the element still runs the browser's own
            // toggle and fires change. Guarding here keeps a field the user
            // cannot reach from writing to formik or calling back into
            // `onChange`, whatever produced the event.
            if (disabled || readOnly) {
              return;
            }
            // `setFieldValue` with `undefined` leaves the key off the submitted
            // body, which is what the indeterminate state should mean.
            setFieldValue(
              name,
              triState ? nextTriStateValue(value) : event.target.checked,
            );
            onChange?.(event, values);
          }}
          type="checkbox"
          className="h-2 w-2 rounded cursor-pointer"
          // Never `undefined` - that would leave the input uncontrolled until
          // the first click and then warn as it switches to controlled.
          checked={value === true}
          aria-checked={
            triState ? (isIndeterminate ? "mixed" : value === true) : undefined
          }
          name={name}
          id={name}
          disabled={readOnly || disabled}
          data-testid={testid}
          {...props}
        />
      </PillarFormInputWrapper>
    </div>
  );
};

export default PillarFormCheckbox;
